import { CommonModule } from '@angular/common';
import {
  Component,
  effect,
  EventEmitter,
  inject,
  OnDestroy,
  OnInit,
  Output,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatAnchor } from '@angular/material/button';
import { MatError, MatFormField, MatInput, MatLabel, MatHint } from '@angular/material/input';
import { RecaptchaV3Module, ReCaptchaV3Service } from 'ng-recaptcha-2';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { RegistrationStore } from '@features/registration/store/registration.store';
import { InputValidators } from '@shared/utils/form_validators.util';
import { environment } from '@env/environment';
import { take, takeUntil } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { ErrorDialogComponent } from '@shared/components/error-dialog/error-dialog.component';

@Component({
  selector: 'app-contact-details-form',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    MatFormField,
    MatLabel,
    MatError,
    MatInput,
    MatAnchor,
    MatProgressSpinner,
    RecaptchaV3Module,
    MatHint,
  ],

  templateUrl: './contact-details-form.html',
  styleUrl: './contact-details-form.scss',
})
export class ContactDetailsForm implements OnInit, OnDestroy {
  @Output() formChange = new EventEmitter<FormGroup>();
  contactDetailsForm = signal<FormGroup>(this._formBuilder.group({}));
  formValue?: ReturnType<typeof toSignal>;
  store = inject(RegistrationStore);

  //fetch otp timer
  isCooldownActive = signal(false);
  cooldownRemaining = signal(0);
  recaptchaKey: string = environment.recaptchaKey;

  private cooldownDuration = 30;
  private cooldownInterval?: ReturnType<typeof setInterval>;
  private _errorDialog = inject(MatDialog);

  constructor(private _formBuilder: FormBuilder, private recaptchaV3Service: ReCaptchaV3Service) {
    this.setupContactDetailsForm();
    this.setupFormEffect();
    this.setupOtpRequestEffect();
    this.setupInvalidOtpEffect();
  }

  ngOnInit(): void {
    this.formChange.emit(this.contactDetailsForm());
  }

  setupContactDetailsForm() {
    const form = this._formBuilder.group({
      mobileNr: [
        '',
        [
          Validators.required,
          InputValidators.numbersOnlyValidator(),
          InputValidators.startsWithZeroValidator(),
          Validators.minLength(10),
          Validators.maxLength(10),
        ],
      ],
      email: ['', [Validators.email]],
      otp: [
        {
          value: '',
          disabled: true,
        },
        [
          Validators.required,
          Validators.pattern('^[0-9]{4}$'),
          Validators.minLength(4),
          Validators.maxLength(4),
        ],
      ],
      otpRequested: [false, [Validators.requiredTrue]],
    });

    this.contactDetailsForm.set(form);
    this.formValue = toSignal(form.valueChanges, { initialValue: form.value });
  }

  setupFormEffect() {
    effect(() => {
      const form = this.contactDetailsForm();
      if (!form || !this.formValue) return;

      const value = this.formValue();
      if (!value) return;

      const mobileValid = form.get('mobileNr')?.valid;

      if (!mobileValid) {
        form.get('otpRequested')?.setValue(false, { emitEvent: false });
        this.store.resetOtpState();
        form.get('otp')?.setValue('', { emitEvent: false });
      }
    });
  }

  setupOtpRequestEffect() {
    effect(() => {
      const otpSent = this.store.otpSent();

      if (otpSent) {
        this.startCooldown();
        this.contactDetailsForm().get('otpRequested')?.setValue(true);
        this.contactDetailsForm().get('otp')?.enable({ emitEvent: false });
      } else {
        this.contactDetailsForm().get('otp')?.disable({ emitEvent: false });
        this.contactDetailsForm().get('otpRequested')?.setValue(false);
      }
    });
  }

  setupInvalidOtpEffect() {
    effect(() => {
      if (this.store.invalidOTP()) {
        const control = this.contactDetailsForm().get('otp');
        control?.setErrors({ ...control.errors, invalidOtp: true });
        control?.markAsTouched();
      }
    });
  }

  onGetOtp() {
    if (this.contactDetailsForm().get('mobileNr')?.invalid || this.isCooldownActive()) return;

    this.recaptchaV3Service
      .execute('action_request_otp')
      .pipe(take(1))
      .subscribe({
        next: (token) => {
          if (token) {
            this.store.requestOtp({
              msisdn: this.contactDetailsForm().get('mobileNr')?.value,
              recaptchaToken: token,
            });
          } else {
            this._errorDialog.open(ErrorDialogComponent, {
              data: {
                errorMessage: 'Something went wrong while requesting OTP. Please try again.',
              },
            });
          }
        },
      });
  }

  private startCooldown() {
    this.isCooldownActive.set(true);
    this.cooldownRemaining.set(this.cooldownDuration);

    this.cooldownInterval = setInterval(() => {
      const remaining = this.cooldownRemaining() - 1;
      if (remaining > 0) {
        this.cooldownRemaining.set(remaining);
      } else {
        this.clearCooldown();
      }
    }, 1000);
  }

  private clearCooldown() {
    clearInterval(this.cooldownInterval);
    this.isCooldownActive.set(false);
    this.cooldownRemaining.set(0);
  }

  ngOnDestroy() {
    if (this.cooldownInterval) {
      clearInterval(this.cooldownInterval);
    }
  }
}
